import { useCallback, useRef, useSyncExternalStore } from "react";
import { referenceEqual } from "../equality";
import type { MemorySliceStore } from "../MemorySliceStore";
import type { MemorySliceDescriptor } from "../types";
import { useLayoutRef } from "./useLayoutRef";

/**
 * Subscribes to a derived portion of a memory slice to limit re-renders.
 *
 * @typeParam TValue Full slice value type.
 * @typeParam TSelected Projected value type.
 * @param store Memory slice store instance.
 * @param slice Slice descriptor.
 * @param selector Projection from full slice value; should be stable (`useCallback`) when non-trivial.
 * @param isEqual Equality for selected values (defaults to `Object.is`).
 * @returns Selected value; re-renders only when the selection changes per `isEqual`.
 */
export const useMemorySliceSelector = <TValue, TSelected>(
    store: MemorySliceStore,
    slice: MemorySliceDescriptor<TValue>,
    selector: ( value: TValue ) => TSelected,
    isEqual: ( left: TSelected, right: TSelected ) => boolean = referenceEqual,
): TSelected => {
    const storeRef = useLayoutRef( store );
    const sliceRef = useLayoutRef( slice );
    const selectorRef = useLayoutRef( selector );
    const isEqualRef = useLayoutRef( isEqual );

    const selectedRef = useRef<TSelected>( selector( store.getSnapshot( slice ) ) );

    const subscribe = useCallback( ( onChange: () => void ) => {
        return storeRef.current.subscribe( sliceRef.current, () => {
            const nextSelection = selectorRef.current( storeRef.current.getSnapshot( sliceRef.current ) );

            if ( !isEqualRef.current( selectedRef.current, nextSelection ) ) {
                selectedRef.current = nextSelection;
                onChange();
            }
        } );
    }, [ isEqualRef, selectorRef, sliceRef, storeRef ] );

    /*
     * `selector` is a live input: callers close over props/state (e.g. a route id)
     * that can change without any store notification. `getSelection` must evaluate
     * the current render's selector — going through `selectorRef` here would read
     * the previous commit's closure (layout effects run after render) and freeze
     * the selection forever when no store notification ever arrives.
     */
    const getSelection = useCallback( (): TSelected => {
        const nextSelection = selector( storeRef.current.getSnapshot( sliceRef.current ) );

        if ( !isEqualRef.current( selectedRef.current, nextSelection ) ) {
            selectedRef.current = nextSelection;
        }

        return selectedRef.current;
    }, [ isEqualRef, selector, sliceRef, storeRef ] );

    return useSyncExternalStore( subscribe, getSelection, getSelection );
};
